import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiLogin, apiForgotPassword, apiVerifyOtp, apiResetPassword } from '../../api/authApi';
import { useAuth } from '../../auth/AuthContext';
import '../../styles/variables.css';
import '../../styles/login.css';

const ROLE_REDIRECT = {
  admin:      '/admin/dashboard',
  instructor: '/instructor/dashboard',
  student:    '/student/dashboard',
};

export default function LoginLayout({ requiredRole }) {
  const { login } = useAuth();
  const navigate  = useNavigate();

  // ── Sign-in state ──
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [error,    setError]    = useState('');
  const [inactive, setInactive] = useState(false);
  const [loading,  setLoading]  = useState(false);

  // ── Forgot-password state ──
  const [view,       setView]       = useState('login');
  const [fpEmail,    setFpEmail]    = useState('');
  const [fpOtp,      setFpOtp]      = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPwd,     setNewPwd]     = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [fpError,    setFpError]    = useState('');
  const [fpLoading,  setFpLoading]  = useState(false);

  // ── Sign-in submit ──
  async function handleSignIn(e) {
    e.preventDefault();
    setError(''); setInactive(false); setLoading(true);
    try {
      const { token, user } = await apiLogin(email.trim().toLowerCase(), password);
      login(token, user);
      navigate(ROLE_REDIRECT[user.role.slug] || '/', { replace: true });
    } catch (err) {
      const msg = err.message || '';
      if (msg.toLowerCase().includes('deactivated') || msg.toLowerCase().includes('contact')) {
        setInactive(true);
      } else {
        setError(msg || 'Invalid email or password');
      }
    } finally { setLoading(false); }
  }

  // ── Forgot — step 1: send OTP ──
  async function handleSendOtp(e) {
    e.preventDefault();
    setFpError(''); setFpLoading(true);
    try {
      await apiForgotPassword(fpEmail.trim().toLowerCase());
      setView('forgot-otp');
    } catch (err) {
      setFpError(err.message);
    } finally { setFpLoading(false); }
  }

  // ── Forgot — step 2: verify OTP ──
  async function handleVerifyOtp(e) {
    e.preventDefault();
    setFpError(''); setFpLoading(true);
    try {
      const d = await apiVerifyOtp(fpEmail.trim().toLowerCase(), fpOtp.trim());
      setResetToken(d.reset_token);
      setView('forgot-newpwd');
    } catch (err) {
      setFpError(err.message);
    } finally { setFpLoading(false); }
  }

  // ── Forgot — step 3: set new password ──
  async function handleResetPassword(e) {
    e.preventDefault();
    setFpError('');
    if (newPwd !== confirmPwd) { setFpError('Passwords do not match'); return; }
    if (newPwd.length < 6)    { setFpError('Password must be at least 6 characters'); return; }
    setFpLoading(true);
    try {
      await apiResetPassword(resetToken, newPwd);
      setView('forgot-done');
    } catch (err) {
      setFpError(err.message);
    } finally { setFpLoading(false); }
  }

  function goBackToLogin() {
    setView('login');
    setFpEmail(''); setFpOtp(''); setResetToken('');
    setNewPwd(''); setConfirmPwd(''); setFpError('');
  }

  // ── Right-panel content by view ──
  const rightPanel = (() => {
    /* ─ Sign In ─ */
    if (view === 'login') return (
      <>
        <div className="lr-eyebrow">Sign In</div>
        <div className="lr-title">Welcome back</div>
        <div className="lr-hint">Enter your credentials to continue to AI Foundry</div>

        {inactive && (
          <div className="login-error" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <strong style={{ fontSize: 13 }}>Account Deactivated</strong>
            <span style={{ fontSize: 12.5, lineHeight: 1.5 }}>
              Your account has been deactivated. Please contact the admin to regain access.
            </span>
          </div>
        )}
        {error && !inactive && <div className="login-error">{error}</div>}

        <form onSubmit={handleSignIn} autoComplete="off">
          <div className="lg">
            <label className="ll-label">Email address</label>
            <input
              className={`li${error ? ' error' : ''}`}
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={e => { setError(''); setInactive(false); setEmail(e.target.value); }}
              required autoFocus
            />
          </div>
          <div className="lg">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <label className="ll-label" style={{ margin: 0 }}>Password</label>
              {requiredRole !== 'admin' && (
                <button
                  type="button"
                  onClick={() => { setView('forgot-email'); setFpEmail(email); }}
                  style={{ background: 'none', border: 'none', padding: 0, fontSize: 12, color: 'var(--yellow)', cursor: 'pointer', fontFamily: "'Nunito Sans', sans-serif", fontWeight: 600, opacity: .85 }}
                >
                  Forgot password?
                </button>
              )}
            </div>
            <input
              className={`li${error ? ' error' : ''}`}
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={e => { setError(''); setInactive(false); setPassword(e.target.value); }}
              required
            />
          </div>
          <button className="sign-btn" type="submit" disabled={loading}>
            {loading ? 'Signing in…' : 'Sign In →'}
          </button>
        </form>
      </>
    );

    /* ─ Step 1: Enter email ─ */
    if (view === 'forgot-email') return (
      <>
        <div className="lr-eyebrow">Forgot Password</div>
        <div className="lr-title">Reset your password</div>
        <div className="lr-hint">Enter your registered email and we'll send you a 6-digit OTP</div>

        {fpError && <div className="login-error">{fpError}</div>}

        <form onSubmit={handleSendOtp} autoComplete="off">
          <div className="lg">
            <label className="ll-label">Email address</label>
            <input
              className="li"
              type="email"
              placeholder="you@example.com"
              value={fpEmail}
              onChange={e => { setFpError(''); setFpEmail(e.target.value); }}
              required autoFocus
            />
          </div>
          <button className="sign-btn" type="submit" disabled={fpLoading}>
            {fpLoading ? 'Sending OTP…' : 'Send OTP →'}
          </button>
        </form>

        <button onClick={goBackToLogin} className="fp-back-btn">
          ← Back to Sign In
        </button>
      </>
    );

    /* ─ Step 2: Enter OTP ─ */
    if (view === 'forgot-otp') return (
      <>
        <div className="lr-eyebrow">Forgot Password</div>
        <div className="lr-title">Enter OTP</div>
        <div className="lr-hint">
          A 6-digit code was sent to <strong style={{ color: 'var(--black)' }}>{fpEmail}</strong>. Valid for 10 minutes.
        </div>

        {fpError && <div className="login-error">{fpError}</div>}

        <form onSubmit={handleVerifyOtp} autoComplete="off">
          <div className="lg">
            <label className="ll-label">One-time code</label>
            <input
              className="li"
              type="text"
              inputMode="numeric"
              maxLength={6}
              placeholder="——————"
              value={fpOtp}
              onChange={e => { setFpError(''); setFpOtp(e.target.value.replace(/\D/g, '')); }}
              required autoFocus
              style={{ letterSpacing: 10, fontSize: 22, textAlign: 'center', fontFamily: "'DM Mono', monospace" }}
            />
          </div>
          <button className="sign-btn" type="submit" disabled={fpLoading || fpOtp.length < 6}>
            {fpLoading ? 'Verifying…' : 'Verify OTP →'}
          </button>
        </form>

        <button
          onClick={() => { setView('forgot-email'); setFpOtp(''); setFpError(''); }}
          className="fp-back-btn"
        >
          ← Change email / Resend
        </button>
      </>
    );

    /* ─ Step 3: New password ─ */
    if (view === 'forgot-newpwd') return (
      <>
        <div className="lr-eyebrow">Forgot Password</div>
        <div className="lr-title">Set new password</div>
        <div className="lr-hint">Choose a strong password of at least 6 characters</div>

        {fpError && <div className="login-error">{fpError}</div>}

        <form onSubmit={handleResetPassword} autoComplete="off">
          <div className="lg">
            <label className="ll-label">New Password</label>
            <input
              className="li"
              type="password"
              placeholder="Minimum 6 characters"
              value={newPwd}
              onChange={e => { setFpError(''); setNewPwd(e.target.value); }}
              required autoFocus
            />
          </div>
          <div className="lg">
            <label className="ll-label">Confirm Password</label>
            <input
              className="li"
              type="password"
              placeholder="Re-enter new password"
              value={confirmPwd}
              onChange={e => { setFpError(''); setConfirmPwd(e.target.value); }}
              required
            />
          </div>
          <button className="sign-btn" type="submit" disabled={fpLoading}>
            {fpLoading ? 'Updating…' : 'Set New Password →'}
          </button>
        </form>
      </>
    );

    /* ─ Done ─ */
    if (view === 'forgot-done') return (
      <>
        <div className="lr-eyebrow">Password Updated</div>
        <div className="lr-title">All done!</div>
        <div className="lr-hint">Your password has been changed successfully.</div>

        <div style={{
          background: 'rgba(26,112,72,.15)',
          border: '1px solid rgba(26,112,72,.3)',
          borderRadius: 8, padding: '14px 16px',
          fontSize: 13.5, color: '#4ADE80', marginBottom: 28, lineHeight: 1.6,
        }}>
          You can now sign in with your new password.
        </div>

        <button className="sign-btn" onClick={goBackToLogin}>
          Back to Sign In →
        </button>
      </>
    );
  })();

  const LogoMark = () => (
    <div className="login-mark">
      <svg viewBox="0 0 14 14" fill="none" width="17" height="17">
        <polygon points="7,1 13,13 1,13" fill="#0C0B09" />
      </svg>
    </div>
  );

  return (
    <div className="login-wrap">

      {/* ── Left panel ── */}
      <div className="login-left">

        <div className="login-desk-logo">
          <LogoMark />
          <span className="login-brand">AI <span>Foundry</span></span>
        </div>

        <div className="login-badge">
          <span className="login-badge-dot" />
          THE LEARNING PLATFORM
        </div>

        <div className="login-tag">
          Learn. Build.<br />
          Lead with
          <em> AI</em>{' '}
          <span className="login-tag-underline"><em className="or">Foundry.</em></span>
        </div>

        <p className="login-sub">
          Your complete AI-powered learning platform — courses, progress
          tracking, quizzes, and certifications all in one place.
        </p>

        <div className="login-features">
          <div className="login-feature-item"><span className="lfi-dot" /> Instructor-led courses</div>
          <div className="login-feature-item"><span className="lfi-dot" /> Real-time progress tracking</div>
          <div className="login-feature-item"><span className="lfi-dot" /> Certifications &amp; achievements</div>
        </div>

      </div>

      {/* ── Right panel ── */}
      <div className="login-right">

        {/* Mobile-only header */}
        <div className="login-mob-header">
          <div className="login-mob-logo">
            <LogoMark />
            <span className="login-brand">AI <span>Foundry</span></span>
          </div>
          <p className="login-mob-tag">Learn. Build. Lead with <em>AI</em></p>
        </div>

        {/* Form card */}
        <div className="login-form-wrap">
          {rightPanel}
        </div>

      </div>

    </div>
  );
}
